import { useState, useRef, useEffect } from 'react';

interface ChatInputProps {
  onSendMessage: (message: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

export function ChatInput({
  onSendMessage,
  disabled = false,
  placeholder = 'Type your message...',
}: ChatInputProps) {
  const [message, setMessage] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && !disabled) {
      onSendMessage(message.trim());
      setMessage('');

      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  return (
    <form onSubmit={handleSubmit} className="border-t border-slate-200 dark:border-slate-700 p-4">
      <div className="flex gap-2">
        <textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={disabled}
          rows={1}
          className="flex-1 resize-none rounded-lg border border-slate-300 dark:border-slate-600
                     bg-white dark:bg-slate-900 px-4 py-2
                     text-slate-900 dark:text-slate-100 placeholder-slate-400
                     focus:outline-none focus:ring-2 focus:ring-slate-400
                     disabled:opacity-50 disabled:cursor-not-allowed
                     max-h-32 overflow-y-auto"
        />

        <button
          type="submit"
          disabled={disabled || !message.trim()}
          className="accent-bg hover:opacity-90 active:scale-[0.98] text-white rounded-lg px-4 py-2
                     font-medium transition-all
                     disabled:opacity-50 disabled:cursor-not-allowed
                     flex-shrink-0"
        >
          Send
        </button>
      </div>

      <p className="text-xs text-slate-500 dark:text-slate-400 mt-2">
        Press Enter to send, Shift+Enter for new line
      </p>
    </form>
  );
}
